import { useCallback, useMemo, useSyncExternalStore } from 'react';

// Binding URL contract (SPEC D3): `?view=<id>` over the D6 destination ids
// plus the owner-only Catalysts desk. Extra params: q,
// discord_auth, verify_email. pushState on view change, replaceState on same
// view, popstate resyncs so Back and Forward are safe.

export const VIEW_IDS = [
  'dashboard',
  'live',
  'checker',
  'discover',
  'desk',
  'catalysts',
  'watchlists',
  'sources',
  'relationships',
  'callers',
  'creators',
  'markets',
  'ledger',
  'health',
  'telegram',
  'discord',
  'x',
  'wallets',
  'coach',
  'alerts',
  'settings',
] as const;

export type ViewId = (typeof VIEW_IDS)[number];

// Dashboard is the landing destination in the D6 Merge cutover (the Calls
// main's home), replacing live.
export const DEFAULT_VIEW: ViewId = 'dashboard';

const VIEW_PARAM = 'view';
const QUERY_PARAM = 'q';
const DISCORD_AUTH_PARAM = 'discord_auth';
const VERIFY_EMAIL_PARAM = 'verify_email';
const NAVIGATE_EVENT = 'aleph:navigate';

export interface ViewState {
  view: ViewId;
  q: string | null;
  discordAuth: string | null;
  verifyEmail: string | null;
}

export interface NavigateOptions {
  replace?: boolean;
  q?: string | null;
  // The hash the next destination should open with. Omitted, a view change
  // starts with no hash at all. Pass it to hand one destination's deep link to
  // another (the tape's "#call=", "Open in Live Calls").
  hash?: string | null;
}

export function isViewId(value: string | null | undefined): value is ViewId {
  return typeof value === 'string' && (VIEW_IDS as readonly string[]).includes(value);
}

function emptyToNull(value: string | null): string | null {
  return value === null || value.trim() === '' ? null : value;
}

export function parseViewState(search: string): ViewState {
  const params = new URLSearchParams(search);
  const rawView = params.get(VIEW_PARAM);
  return {
    view: isViewId(rawView) ? rawView : DEFAULT_VIEW,
    q: emptyToNull(params.get(QUERY_PARAM)),
    discordAuth: emptyToNull(params.get(DISCORD_AUTH_PARAM)),
    verifyEmail: emptyToNull(params.get(VERIFY_EMAIL_PARAM)),
  };
}

export function buildSearch(current: string, next: ViewId, options: NavigateOptions = {}): string {
  const params = new URLSearchParams(current);
  params.set(VIEW_PARAM, next);
  if (options.q !== undefined) {
    if (options.q === null || options.q.trim() === '') {
      params.delete(QUERY_PARAM);
    } else {
      params.set(QUERY_PARAM, options.q);
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

function readSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener(NAVIGATE_EVENT, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(NAVIGATE_EVENT, onChange);
  };
}

// The hash holds one destination's own state (tab, window, layout, selected
// call). Carrying it across a destination change applied filters the reader
// never chose on the screen they landed on: Sources at "#win=24h" opened
// Relationships on a 24 h window and dropped it from 99 relations to 44. So a
// view change starts with no hash unless the caller hands one over.
export function nextHashFor(
  currentHash: string,
  sameView: boolean,
  requested: string | null | undefined,
): string {
  if (requested === undefined) return sameView ? currentHash : '';
  if (requested === null || requested === '' || requested === '#') return '';
  return requested.startsWith('#') ? requested : `#${requested}`;
}

function commitSearch(nextSearch: string, replace: boolean, nextHash: string): void {
  const url = `${window.location.pathname}${nextSearch}${nextHash}`;
  if (replace) {
    window.history.replaceState(window.history.state, '', url);
  } else {
    window.history.pushState(window.history.state, '', url);
  }
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

export function useView() {
  const search = useSyncExternalStore(subscribe, readSearch, () => '');
  const state = useMemo(() => parseViewState(search), [search]);

  const navigate = useCallback((next: ViewId, options: NavigateOptions = {}) => {
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    const nextSearch = buildSearch(currentSearch, next, options);
    const sameView = parseViewState(currentSearch).view === next;
    const nextHash = nextHashFor(currentHash, sameView, options.hash);
    if (nextSearch === currentSearch && nextHash === currentHash) {
      return;
    }
    commitSearch(nextSearch, options.replace ?? sameView, nextHash);
  }, []);

  const clearParams = useCallback((names: ReadonlyArray<'q' | 'discord_auth' | 'verify_email'>) => {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const name of names) {
      if (params.has(name)) {
        params.delete(name);
        changed = true;
      }
    }
    if (!changed) {
      return;
    }
    const serialized = params.toString();
    // Same destination, so its own hash stays.
    commitSearch(serialized ? `?${serialized}` : '', true, window.location.hash);
  }, []);

  return { ...state, navigate, clearParams };
}
